"use client";

import React from "react";
import { Spinner } from "@heroui/react";

import { MarkdownWeBuild } from "@/components/MarkdownWeBuild";
import { logger } from "@/telemetry";

export interface MarkdownViewerProps {
  /** Markdown document as ArrayBuffer */
  documentData: ArrayBuffer;
}

/**
 * Markdown Viewer component
 * Renders markdown files with full formatting support
 */
export const MarkdownViewer: React.FC<MarkdownViewerProps> = ({ documentData }) => {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [markdownContent, setMarkdownContent] = React.useState<string>("");

  // Convert ArrayBuffer to text
  React.useEffect(() => {
    if (!documentData) {
      return;
    }

    let isMounted = true;

    const loadMarkdown = () => {
      try {
        setLoading(true);
        setError(null);

        // Decode ArrayBuffer as UTF-8 text
        const decoder = new TextDecoder("utf-8");
        const text = decoder.decode(documentData);

        if (!isMounted) return;

        setMarkdownContent(text);
        setLoading(false);
      } catch (err) {
        logger.error("Failed to load Markdown", { error: String(err) });
        if (isMounted) {
          setError("Failed to load markdown file");
          setLoading(false);
        }
      }
    };

    loadMarkdown();

    return () => {
      isMounted = false;
    };
  }, [documentData]);

  return (
    <div className="w-full h-[75vh] overflow-auto bg-content1 p-8">
      {/* Loading overlay */}
      {loading && (
        <div className="flex items-center justify-center h-[60vh]">
          <Spinner label="Loading markdown file..." />
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-danger-500">{error}</p>
        </div>
      )}

      {/* Markdown content */}
      {!loading && !error && markdownContent && (
        <div className="markdown-container max-w-4xl mx-auto">
          <MarkdownWeBuild>{markdownContent}</MarkdownWeBuild>
        </div>
      )}
    </div>
  );
};

export default MarkdownViewer;
